"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvsInternalAccountsQuery } from "@/lib/queries";
import { TagsIcon } from "lucide-react";

/** Reference / channel `sellingType` values (display and matching against `AvsInternalAccount.sellingType`). */
export const AVS_SELLING_TYPE_VDL = "VDL" as const;
export const AVS_SELLING_TYPE_VDS = "VDS" as const;
export const AVS_SELLING_TYPE_FLOTTE = "Flotte" as const;

export const SALES_TYPE_VALUES = [
  AVS_SELLING_TYPE_VDL,
  AVS_SELLING_TYPE_VDS,
  AVS_SELLING_TYPE_FLOTTE,
] as const;
export type SalesTypeValue = (typeof SALES_TYPE_VALUES)[number];

function sellingTypeMatchesChannel(
  accountSellingType: string,
  channel: SalesTypeValue,
): boolean {
  return accountSellingType.toLowerCase() === channel.toLowerCase();
}

const OPTIONS: ReadonlyArray<{
  value: SalesTypeValue;
  title: string;
}> = [
  {
    value: AVS_SELLING_TYPE_VDL,
    title: "VDL - Véhicule de location",
  },
  {
    value: AVS_SELLING_TYPE_VDS,
    title: "VDS - Véhicule de service",
  },
  {
    value: AVS_SELLING_TYPE_FLOTTE,
    title: "Flotte",
  },
];

export interface SalesChannelsProps {
  /** Controlled mode: when set, the parent drives this value. */
  value?: SalesTypeValue | "";
  defaultValue?: SalesTypeValue | "";
  onValueChange?: (value: SalesTypeValue) => void;
  /** Sub-select value under the selected channel (controlled mode). */
  subSelectValue?: string;
  defaultSubSelectValue?: string;
  onSubSelectValueChange?: (value: string) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  /** Prefix for accessible `id`s when multiple instances exist on one page. */
  fieldIdPrefix?: string;
}

export function AvsSalesChannelsStep({
  value: controlledValue,
  defaultValue = "",
  onValueChange,
  subSelectValue: controlledSubValue,
  defaultSubSelectValue = "",
  onSubSelectValueChange,
  onCanProceedChange,
  fieldIdPrefix = "sales-type",
}: SalesChannelsProps) {
  const [uncontrolled, setUncontrolled] = useState<SalesTypeValue | "">(
    defaultValue,
  );
  const [uncontrolledSub, setUncontrolledSub] = useState(defaultSubSelectValue);
  const selected =
    controlledValue !== undefined ? controlledValue : uncontrolled;
  const rawSubSelect =
    controlledSubValue !== undefined ? controlledSubValue : uncontrolledSub;

  const setSelected = useCallback(
    (next: SalesTypeValue) => {
      if (controlledValue === undefined) {
        if (uncontrolled !== next && controlledSubValue === undefined) {
          setUncontrolledSub("");
        }
        setUncontrolled(next);
      }
      onValueChange?.(next);
    },
    [controlledValue, controlledSubValue, uncontrolled, onValueChange],
  );

  const setSubSelected = useCallback(
    (next: string) => {
      if (controlledSubValue === undefined) {
        setUncontrolledSub(next);
      }
      onSubSelectValueChange?.(next);
    },
    [controlledSubValue, onSubSelectValueChange],
  );

  const avsInternalAccountsQuery = useAvsInternalAccountsQuery({
    sort: { label: "asc" },
  });

  const subOptionsBySellingType = useMemo(() => {
    const accounts = avsInternalAccountsQuery.data?.avsInternalAccounts ?? [];
    const map = new Map<
      SalesTypeValue,
      ReadonlyArray<{ value: string; label: string }>
    >();
    for (const channel of SALES_TYPE_VALUES) {
      const subOptions = accounts
        .filter(
          (a) =>
            sellingTypeMatchesChannel(a.sellingType, channel) && a.isActive,
        )
        .map((a) => ({ value: String(a.id), label: a.label }));
      map.set(channel, subOptions);
    }
    return map;
  }, [avsInternalAccountsQuery.data?.avsInternalAccounts]);
  const hasAnyActiveInternalAccount = useMemo(() => {
    const accounts = avsInternalAccountsQuery.data?.avsInternalAccounts ?? [];
    return accounts.some((account) => account.isActive);
  }, [avsInternalAccountsQuery.data?.avsInternalAccounts]);

  const activeOption = OPTIONS.find((o) => o.value === selected);
  const activeSubOptions = activeOption
    ? (subOptionsBySellingType.get(activeOption.value) ?? [])
    : [];
  const subMatchesChannel =
    !!activeOption && activeSubOptions.some((s) => s.value === rawSubSelect);
  const subForSelect = subMatchesChannel ? rawSubSelect : "";
  const selectedAccountOption = activeSubOptions.find(
    (option) => option.value === subForSelect,
  );
  const isGloballyBlocked =
    !avsInternalAccountsQuery.isPending &&
    !avsInternalAccountsQuery.isError &&
    !hasAnyActiveInternalAccount;
  const isAccountSelectionDisabled = !selected || isGloballyBlocked;

  const canProceed =
    selected === AVS_SELLING_TYPE_FLOTTE ||
    ((selected === AVS_SELLING_TYPE_VDL || selected === AVS_SELLING_TYPE_VDS) &&
      subMatchesChannel);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card className="border-border">
      <CardHeader className="space-y-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TagsIcon className="text-primary h-5 w-5 shrink-0" aria-hidden />
          Canal de vente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-8 pt-2">
        <div className="space-y-3">
          <Label className="text-sm font-semibold">
            Choix du canal de vente <span className="text-destructive">*</span>
          </Label>
          {isGloballyBlocked ? (
            <p className="text-destructive text-sm" role="alert">
              Aucun compte interne actif disponible. Veuillez vérifier la liste
              des comptes internes.
            </p>
          ) : null}

          <RadioGroup
            value={selected || undefined}
            onValueChange={(v) => setSelected(v as SalesTypeValue)}
            className="gap-3"
          >
            {OPTIONS.map((opt) => {
              const id = `${fieldIdPrefix}-${opt.value.toLowerCase()}`;
              return (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={opt.value}
                    id={id}
                    disabled={isGloballyBlocked}
                  />
                  <Label htmlFor={id}>{opt.title}</Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>
        <div className="space-y-3">
          <Label className="text-sm font-semibold">
            Choix du compte <span className="text-destructive">*</span>
          </Label>
          {!selected ? (
            <Combobox
              items={[]}
              value={null}
              onValueChange={() => {
                return;
              }}
            >
              <ComboboxInput
                id={`${fieldIdPrefix}-account`}
                className="w-full max-w-md"
                placeholder="Sélectionner un compte"
                disabled
                aria-label="Choix du compte"
              />
            </Combobox>
          ) : avsInternalAccountsQuery.isPending ? (
            <Skeleton className="h-10 w-full max-w-md rounded-md" aria-busy />
          ) : avsInternalAccountsQuery.isError ? (
            <p className="text-destructive text-sm" role="alert">
              Impossible de charger les comptes internes.
            </p>
          ) : activeSubOptions.length === 0 ? (
            <p className="text-destructive text-sm" role="alert">
              Aucun compte interne actif n&apos;est disponible pour ce canal.
              Veuillez vérifier la listes des comptes internes.
            </p>
          ) : (
            <Combobox
              items={activeSubOptions}
              itemToStringLabel={(item) => item.label}
              value={selectedAccountOption ?? null}
              onValueChange={(next) => setSubSelected(next?.value ?? "")}
            >
              <ComboboxInput
                id={`${fieldIdPrefix}-account`}
                className="w-full max-w-md"
                placeholder="Sélectionner un compte"
                showClear={subForSelect !== ""}
                disabled={isAccountSelectionDisabled}
                aria-label="Choix du compte"
              />
              <ComboboxContent>
                <ComboboxEmpty>Aucun compte interne trouvé.</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
